import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollTopButton from '@/components/feature/ScrollTopButton';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import StatusBadge from '@/components/feature/StatusBadge';
import ArticleTimeline from './components/ArticleTimeline';
import MaterialList from './components/MaterialList';
import RelatedArticles from './components/RelatedArticles';
import { getArticleBySlug } from '@/mocks/articles';
import { articleSections } from '@/mocks/articleSections';
import { categories, site } from '@/mocks/site';
import { useSeo } from '@/hooks/useSeo';

export default function Article() {
  const { slug = '' } = useParams();
  const article = getArticleBySlug(slug);
  const sections = article ? articleSections[article.slug] || [] : [];

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  useSeo({
    title: article
      ? `${article.title} - ${article.categoryName}网曝黑料档案 | 瓜田档案局`
      : '档案未找到 | 瓜田档案局',
    description: article
      ? `${article.excerpt.slice(0, 90)} 含完整时间线、录音与聊天记录素材清单。`
      : '你访问的档案不存在或已被移除，可返回合集库浏览其他内容。',
    keywords: article
      ? `${article.tags.join(',')},网曝黑料,黑料时间线,黑料合集`
      : '网曝黑料,黑料合集',
    path: `/article/${slug}`,
    type: 'article',
    jsonLd: article
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: { '@type': 'Organization', name: site.name },
            publisher: { '@type': 'Organization', name: site.name },
            articleSection: article.categoryName,
            keywords: article.tags.join(','),
            url: `https://ddysyjy.cn/article/${article.slug}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://ddysyjy.cn/' },
              { '@type': 'ListItem', position: 2, name: '爆料合集', item: 'https://ddysyjy.cn/collections' },
              { '@type': 'ListItem', position: 3, name: article.title, item: `https://ddysyjy.cn/article/${article.slug}` },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex max-w-content flex-col items-center px-4 py-32 text-center md:px-6">
          <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这份档案不存在</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            它可能已被合并或更新了链接。你可以回到合集库，从最新的档案开始翻。
          </p>
          <Link
            to="/collections"
            className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回合集库
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const category = categories.find((item) => item.id === article.category);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <article>
          <header className="w-full border-b border-background-200 bg-background-100">
            <div className="mx-auto max-w-content px-4 pt-28 pb-8 md:px-6 md:pt-36 md:pb-12">
              <Breadcrumbs
                items={[
                  { label: '首页', to: '/' },
                  { label: '爆料合集', to: '/collections' },
                  { label: category?.name || article.categoryName, to: `/collections?cat=${article.category}` },
                  { label: article.title },
                ]}
              />
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                  {article.categoryName}
                </span>
                <StatusBadge status={article.status} />
              </div>
              <h1 className="mt-5 max-w-4xl font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                {article.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-600">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  阅读约 {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line text-primary-500"></i>
                  {article.heat} 热度
                </span>
              </div>
            </div>
          </header>

          <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <div className="w-full overflow-hidden rounded-lg bg-background-200">
                  <img
                    src={article.cover}
                    alt={`${article.title} - ${article.categoryName}网曝黑料档案封面`}
                    title={`${article.title} 时间线整理`}
                    className="w-full h-64 object-cover object-top md:h-96"
                  />
                </div>

                <div className="mt-8 rounded-lg border-l-4 border-primary-500 bg-background-100 p-5">
                  <p className="text-sm leading-relaxed text-foreground-700 md:text-base">
                    {article.excerpt}
                  </p>
                </div>

                <div className="mt-10 space-y-10">
                  {sections.map((section, index) => (
                    <section key={section.heading} id={`section-${index + 1}`}>
                      <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                        <span className="mr-2 text-primary-500">{String(index + 1).padStart(2, '0')}</span>
                        {section.heading}
                      </h2>
                      <div className="mt-4 space-y-4">
                        {section.paragraphs.map((p) => (
                          <p key={p.slice(0, 20)} className="text-sm leading-loose text-foreground-700 md:text-base">
                            {p}
                          </p>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <aside className="lg:col-span-1">
                <div className="lg:sticky lg:top-24 space-y-6">
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h2 className="font-heading text-base font-bold text-foreground-950">档案信息</h2>
                    <dl className="mt-4 space-y-3 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <dt className="text-foreground-500">当前状态</dt>
                        <dd>
                          <StatusBadge status={article.status} />
                        </dd>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <dt className="text-foreground-500">所属圈子</dt>
                        <dd className="text-foreground-800">{article.categoryName}</dd>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <dt className="text-foreground-500">时间节点</dt>
                        <dd className="text-foreground-800">{article.timeline.length} 条</dd>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <dt className="text-foreground-500">素材数量</dt>
                        <dd className="text-foreground-800">{article.materials.length} 份</dd>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <dt className="text-foreground-500">最近更新</dt>
                        <dd className="text-foreground-800">{article.date}</dd>
                      </div>
                    </dl>
                  </div>

                  <nav aria-label="内容目录" className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h2 className="font-heading text-base font-bold text-foreground-950">本篇目录</h2>
                    <ol className="mt-4 space-y-3 text-sm">
                      {sections.map((section, index) => (
                        <li key={section.heading}>
                          <a
                            href={`#section-${index + 1}`}
                            className="flex gap-2 text-foreground-600 transition-colors hover:text-primary-600"
                          >
                            <span className="font-heading text-foreground-400">{String(index + 1).padStart(2, '0')}</span>
                            <span>{section.heading}</span>
                          </a>
                        </li>
                      ))}
                      <li>
                        <a
                          href="#archive-timeline"
                          className="flex gap-2 text-foreground-600 transition-colors hover:text-primary-600"
                        >
                          <span className="font-heading text-foreground-400">05</span>
                          <span>事件时间线</span>
                        </a>
                      </li>
                    </ol>
                  </nav>
                </div>
              </aside>
            </div>

            <section id="archive-timeline" className="mt-14 border-t border-background-200 pt-10">
              <h2 className="mb-6 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                事件时间线
              </h2>
              <ArticleTimeline events={article.timeline} />
            </section>

            <section className="mt-14 border-t border-background-200 pt-10">
              <h2 className="mb-6 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                素材清单
              </h2>
              <MaterialList materials={article.materials} />
              <p className="mt-4 text-xs leading-relaxed text-foreground-500">{site.disclaimer}</p>
            </section>

            <div className="mt-14">
              <RelatedArticles slugs={article.related} />
            </div>
          </div>
        </article>
      </main>
      <SiteFooter />
      <ScrollTopButton />
    </div>
  );
}